import React, { useImperativeHandle, useRef, useState } from "react";
import { ScrollView, Text, TouchableOpacity, View } from "react-native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { Feather } from "@expo/vector-icons";

import { useTheme } from "../../../ThemeProvider.tsx";
import Loader from "../../../components/Loader";
import ModalConfirmacao from "../../../components/ModalConfirmacao";
import { corTextoSobre } from "../../theme/cores";
import styles, { CORES } from "./styles";

// Template do fluxo de agendamento no layout novo (fundo creme + cards brancos).
// O template antigo (src/template/Index.tsx) continua sendo usado pelo modo
// legado (utilizaPwaSimples / versaoPWA 2 / tema escuro) e pela área do cliente.

interface Props {
  titulo: string;
  subtitulo?: string;
  etapaAtual?: number;
  totalEtapas?: number;
  rotuloEtapa?: string; // sobrescreve o "Etapa X de Y"
  contadorEtapa?: string; // ex.: "1/2", exibido à direita quando não há botão de fechar
  avisoRodape?: string; // texto pequeno acima do botão (ex.: pagamento seguro)
  conteudoTopo?: React.ReactNode; // conteúdo logo abaixo do título (ex.: aviso de erro)
  filtro?: React.ReactNode;
  children?: React.ReactNode;

  // rodapé
  resumoRotulo?: string;
  resumoValor?: string;
  textoBotao?: string;
  iconeBotao?: string;
  desabilitarBotao?: boolean;
  ocultarRodape?: boolean;
  onBotaoClick?: () => void;

  onVoltarClick?: () => void;
  ocultarVoltar?: boolean;
  ocultarFechar?: boolean;
}

function TemplateEtapas(
  {
    titulo,
    subtitulo,
    etapaAtual,
    totalEtapas,
    rotuloEtapa,
    contadorEtapa,
    avisoRodape,
    conteudoTopo,
    filtro,
    children,
    resumoRotulo,
    resumoValor,
    textoBotao,
    iconeBotao,
    desabilitarBotao,
    ocultarRodape,
    onBotaoClick,
    onVoltarClick,
    ocultarVoltar,
    ocultarFechar,
  }: Props,
  ref: any,
) {
  const { primaryColor } = useTheme();
  const corPrincipal: string = primaryColor || "#006400";
  const corTextoBotao = corTextoSobre(corPrincipal);

  const navigation = useNavigation<StackNavigationProp<any>>();
  const refModal = useRef<any>(null);
  const [tituloModal, setTituloModal] = useState<string>("Atenção");
  const [textoModal, setTextoModal] = useState<string>("");
  const [abrirLoader, setAbrirLoader] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    abrirModal(mensagem: string, titulo: string) {
      setTituloModal(titulo ? titulo : "Atenção");
      setTextoModal(mensagem);
      refModal.current.abrirModal();
    },
    fecharModal() {
      refModal.current.fecharModal();
    },
    abrirLoader() {
      setAbrirLoader(true);
    },
    fecharLoader() {
      setAbrirLoader(false);
    },
  }));

  function voltarInicio() {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "TabNavigator", params: { screen: "Inicio" } }],
      }),
    );
  }

  const textoEtapa =
    rotuloEtapa ??
    (etapaAtual && totalEtapas ? `Etapa ${etapaAtual} de ${totalEtapas}` : "");

  return (
    <View style={styles.container}>
      <View style={styles.larguraConteudo}>
        {/* Barra superior */}
        <View style={styles.barraSuperior}>
          {!ocultarVoltar ? (
            <TouchableOpacity
              style={styles.botaoQuadrado}
              onPress={() =>
                onVoltarClick ? onVoltarClick() : navigation.goBack()
              }
            >
              <Feather name="chevron-left" size={20} color={CORES.titulo} />
            </TouchableOpacity>
          ) : (
            <View style={styles.espacoBotao} />
          )}

          <Text style={styles.textoEtapa}>{textoEtapa}</Text>

          {!ocultarFechar ? (
            <TouchableOpacity style={styles.botaoQuadrado} onPress={voltarInicio}>
              <Feather name="x" size={18} color={CORES.titulo} />
            </TouchableOpacity>
          ) : contadorEtapa ? (
            <View style={[styles.espacoBotao, styles.contador]}>
              <Text style={styles.textoContador}>{contadorEtapa}</Text>
            </View>
          ) : (
            <View style={styles.espacoBotao} />
          )}
        </View>

        {/* Progresso */}
        {!!etapaAtual && !!totalEtapas && (
          <View style={styles.linhaProgresso}>
            {Array.from({ length: totalEtapas }).map((_, index) => (
              <View
                key={index}
                style={[
                  styles.segmentoProgresso,
                  {
                    backgroundColor:
                      index < etapaAtual ? corPrincipal : CORES.progressoVazio,
                  },
                ]}
              />
            ))}
          </View>
        )}

        {/* Título */}
        <Text style={styles.titulo}>{titulo}</Text>
        {!!subtitulo && <Text style={styles.subtitulo}>{subtitulo}</Text>}

        {conteudoTopo}

        {!!filtro && <View style={styles.filtro}>{filtro}</View>}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollConteudo}
      >
        <View style={styles.larguraConteudo}>{children}</View>
      </ScrollView>

      {!ocultarRodape && (
        <View style={styles.rodape}>
          {!!avisoRodape && (
            <View style={[styles.larguraConteudo, styles.linhaAviso]}>
              <Feather name="lock" size={11} color={CORES.subtitulo} />
              <Text style={styles.textoAviso}>{avisoRodape}</Text>
            </View>
          )}
          <View style={[styles.larguraConteudo, styles.linhaRodape]}>
            {(!!resumoRotulo || !!resumoValor) && (
              <View style={styles.resumo}>
                {!!resumoRotulo && (
                  <Text style={styles.resumoRotulo}>{resumoRotulo}</Text>
                )}
                {!!resumoValor && (
                  <Text style={styles.resumoValor} numberOfLines={1}>
                    {resumoValor}
                  </Text>
                )}
              </View>
            )}

            <TouchableOpacity
              activeOpacity={0.85}
              disabled={desabilitarBotao}
              onPress={onBotaoClick}
              style={[
                styles.botaoPrincipal,
                {
                  backgroundColor: corPrincipal,
                  opacity: desabilitarBotao ? 0.45 : 1,
                },
              ]}
            >
              <Text
                style={[styles.textoBotaoPrincipal, { color: corTextoBotao }]}
                numberOfLines={1}
              >
                {textoBotao || "Continuar"}
              </Text>
              <Feather
                name={(iconeBotao || "arrow-right") as any}
                size={18}
                color={corTextoBotao}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}

      <ModalConfirmacao
        titulo={tituloModal}
        ref={refModal}
        mensagem={textoModal}
        textoBotaoPositivo={"OK"}
        onConfirmPositivo={() => {
          if (refModal.current) {
            refModal.current.fecharModal();
          }
        }}
      />

      {abrirLoader && <Loader />}
    </View>
  );
}

export default React.forwardRef(TemplateEtapas);
